/**
 * Infinite scroll yardımcıları.
 *
 * Backend DRF PageNumberPagination (PAGE_SIZE=20) döndürür; liste sayfaları
 * `useInfiniteResource` ile sayfa sayfa çeker. Bu dosya iki şey sağlar:
 *
 *  - useInfiniteScrollRef: tablo/listenin sonuna konan bir "sentinel"
 *    elemanı görünür olunca sonraki sayfayı çeken IntersectionObserver hook'u.
 *  - InfiniteTable: başlık + satır render + sentinel + boş/yükleniyor
 *    durumlarını saran generic Mantine Table sarmalayıcısı.
 */
import { Center, Group, Loader, Table, Text, UnstyledButton } from "@mantine/core";
import { IconArrowsSort, IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

/**
 * Sentinel ref'i döndürür. Eleman viewport'a (300px önceden) girince ve
 * başka sayfa varsa `fetchNextPage` çağrılır.
 */
export function useInfiniteScrollRef<T extends HTMLElement>(opts: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /** Liste uzunluğu — yeni satır eklendiğinde observer'ı yeniden bağlar. */
  itemCount: number;
  /**
   * İç kaydırma kabı (örn. Mantine ScrollArea viewport'u). Sentinel bir
   * ScrollArea/overflow div'inin İÇİNDEYSE bu verilmeli: aksi halde observer
   * varsayılan olarak tarayıcı viewport'unu (root: null) ölçer; iç kapta 420px
   * gibi sınırlı bir alanda sentinel, viewport'a göre HER ZAMAN "görünür"
   * sayılır → fetchNextPage durmadan tetiklenir, spinner sonsuz döner.
   */
  root?: HTMLElement | null;
}) {
  const { hasNextPage, isFetchingNextPage, fetchNextPage, itemCount, root } = opts;
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !hasNextPage) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, { root: root ?? null, rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, itemCount, root]);

  return ref;
}

export interface InfiniteTableColumn<T> {
  /** Başlık metni. */
  header: ReactNode;
  /** Hücre içeriği. */
  render: (row: T) => ReactNode;
  /** Hücre + başlık hizası. */
  align?: "left" | "right" | "center";
  /** İsteğe bağlı sabit genişlik. */
  width?: number | string;
  /**
   * Sunucu tarafı sıralama alanı (DRF `ordering=` değeri, örn. "starts_at",
   * "table__name"). Verilirse başlık tıklanabilir olur ve `onSortChange`
   * tetiklenir. Sıralama durumu `sort`/`onSortChange` props'undan gelir.
   */
  sortKey?: string;
}

export interface InfiniteTableProps<T> {
  columns: InfiniteTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** İlk sayfa yükleniyor mu. */
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /** Satıra tıklama (detay drawer açma vb.). */
  onRowClick?: (row: T) => void;
  /** Satır bazlı inline style — vurgulama (highlight) gibi. */
  rowStyle?: (row: T) => CSSProperties | undefined;
  /** Sonuç boşken gösterilecek içerik. */
  emptyContent?: ReactNode;
  striped?: boolean;
  /** Tablonun sıkışmadan kalabileceği min genişlik. Dar ekranda (mobil)
   *  bunun altına inince yatay kaydırma çubuğu çıkar, sütunlar kesilmez. */
  minWidth?: number | string;
  /**
   * Tablonun kendi içinde kaydırılacağı kap modu:
   *  - "fill"  → kap, ebeveyninin kalan yüksekliğini doldurur (flex:1,
   *    minHeight:0). Ebeveyn viewport yüksekliğini sınırlamışsa (sayfa flex
   *    kolon + Main scroll kapalı) TEK kaydırma çubuğu olur: içerik sığarsa
   *    çubuk YOK, sığmazsa yalnız tablo kayar. ÇİFT scroll olmaz.
   *  - bir CSS değeri (ör. "60vh") → maxHeight olarak uygulanır.
   *  - null → eski davranış: yalnız yatay kaydırma, dikeyi sayfa yapar.
   * Her iki kap modunda da başlık sticky + yatay çubuk kabın altında sabit.
   * Default: null (geriye uyumlu). Products gibi flex-fill layout'a hazır
   * sayfalar açıkça "fill" verir.
   */
  maxBodyHeight?: string | null;
  /**
   * Aktif sıralama — DRF `ordering` formatı: "starts_at" (artan) veya
   * "-starts_at" (azalan). null/undefined = sıralama yok. `sortKey` tanımlı
   * kolonlarla birlikte kullanılır.
   */
  sort?: string | null;
  /**
   * Bir sıralanabilir başlığa tıklanınca çağrılır; yeni `ordering` değerini
   * verir (aynı sütun tekrar tıklanınca yön değişir, sonra sıralama kapanır).
   */
  onSortChange?: (next: string | null) => void;
}

/**
 * Bir sütun başlığına tıklandığında bir sonraki ordering değerini üretir:
 * yoksa artan → azalan → kapalı (3'lü döngü). Farklı sütuna geçince artan.
 */
function nextSort(current: string | null | undefined, key: string): string | null {
  if (current === key) return `-${key}`;       // artan → azalan
  if (current === `-${key}`) return null;       // azalan → kapalı
  return key;                                   // kapalı / başka sütun → artan
}

/**
 * Sayfalanmış bir listeyi infinite-scroll tablo olarak çizer.
 * Son satırdan sonra görünmez bir sentinel satır vardır — viewport'a
 * girince sonraki sayfa çekilir, altında spinner gösterilir.
 */
export function InfiniteTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  onRowClick,
  rowStyle,
  emptyContent,
  striped,
  minWidth = 560,
  maxBodyHeight = null,
  sort,
  onSortChange,
}: InfiniteTableProps<T>) {
  // Bounded mode: tablo kendi içinde dikey kaydırılır → observer'ın root'u bu
  // kap olmalı (aksi halde sentinel iç kapta hep "görünür" sayılıp sonsuz
  // fetch tetikler). Kap mount olunca state'e yazıp observer'ı yeniden bağlarız.
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const bounded = !!maxBodyHeight;
  const sentinelRef = useInfiniteScrollRef<HTMLTableRowElement>({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    itemCount: rows.length,
    root: bounded ? scrollRoot : null,
  });

  if (isLoading) {
    return <Center py="xl"><Loader /></Center>;
  }
  if (rows.length === 0) {
    return (
      <Center py="xl">
        {emptyContent ?? <Text size="sm" c="dimmed">Kayıt yok.</Text>}
      </Center>
    );
  }

  const table = (
      <Table striped={!!striped} highlightOnHover stickyHeader={bounded}>
        <Table.Thead>
          <Table.Tr>
            {columns.map((c, i) => {
              const sortable = !!c.sortKey && !!onSortChange;
              if (!sortable) {
                return (
                  <Table.Th key={i} ta={c.align} w={c.width}>
                    {c.header}
                  </Table.Th>
                );
              }
              const active = sort === c.sortKey || sort === `-${c.sortKey}`;
              const desc = sort === `-${c.sortKey}`;
              return (
                <Table.Th key={i} ta={c.align} w={c.width}>
                  <UnstyledButton
                    onClick={() => onSortChange!(nextSort(sort, c.sortKey!))}
                    style={{ width: "100%" }}
                  >
                    <Group
                      gap={4}
                      wrap="nowrap"
                      justify={
                        c.align === "right" ? "flex-end"
                          : c.align === "center" ? "center" : "flex-start"
                      }
                    >
                      <Text size="sm" fw={700} span>{c.header}</Text>
                      {!active ? (
                        <IconArrowsSort size={14} style={{ opacity: 0.4 }} />
                      ) : desc ? (
                        <IconChevronDown size={14} />
                      ) : (
                        <IconChevronUp size={14} />
                      )}
                    </Group>
                  </UnstyledButton>
                </Table.Th>
              );
            })}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr
              key={rowKey(row)}
              style={{
                ...(onRowClick ? { cursor: "pointer" } : {}),
                ...(rowStyle ? rowStyle(row) : {}),
              }}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((c, i) => (
                <Table.Td key={i} ta={c.align}>
                  {c.render(row)}
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
          {hasNextPage && (
            <Table.Tr ref={sentinelRef}>
              <Table.Td colSpan={columns.length}>
                <Center py="sm"><Loader size="xs" /></Center>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
  );

  // Bounded mod: tablo kendi içinde HEM dikey HEM yatay kaydırılır. Yatay
  // kaydırma çubuğu kabın alt kenarında sabit kalır → dar ekranda sağdaki
  // sütunlara erişilir; başlık sticky olduğu için kaybolmaz. "fill" modunda
  // kap ebeveynin kalan yüksekliğini doldurur (flex:1, minHeight:0) → çift
  // dikey scroll oluşmaz (ebeveyn flex kolon + sayfa scroll kapalıysa).
  if (bounded) {
    const fill = maxBodyHeight === "fill";
    return (
      <div
        ref={setScrollRoot}
        style={
          fill
            ? { flex: 1, minHeight: 0, overflow: "auto" }
            : { maxHeight: maxBodyHeight!, overflow: "auto" }
        }
      >
        <div style={{ minWidth }}>{table}</div>
      </div>
    );
  }

  // Legacy: yalnız yatay kaydırma; sayfa gövdesi dikey kaydırılır.
  return (
    <Table.ScrollContainer minWidth={minWidth}>{table}</Table.ScrollContainer>
  );
}
